// 登入/註冊頁共用「教學輪播」視窗（9 張卡，自動輪播 + 點圖放大 + 不要再顯示）
//   mode="login"  → 標題/主按鈕用「登入」版
//   mode="signup" → 標題/主按鈕用「註冊」版
//   dismissKey：勾「不要再顯示」時寫入的 localStorage key（各頁獨立，父層用同一 key 判斷是否開啟）
import { useState, useEffect, useContext, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MobileStepper,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS, TYPO, RADIUS } from "theme/tokens";

export default function IntroCarouselModal({ open, onClose, mode = "login", dismissKey }) {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("IntroCarouselModal", "authentication/IntroCarouselModal/IntroCarouselModal_lang");
  }, [language, loadLanguageFile]);
  const L = useMemo(
    () => componentLanguageData.IntroCarouselModal?.Main || {},
    [componentLanguageData]
  );

  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);

  const slides = useMemo(() => {
    const S = L.Slides || {};
    // 圖片依語系切換：英文(en-*)→ /carousel/en，其餘(zh-*)→ /carousel/tw
    const imgLang = language && language.startsWith("en") ? "en" : "tw";
    return Array.from({ length: 9 }, (_, i) => {
      const n = i + 1;
      return {
        img: `/images/carousel/${imgLang}/${n}.png`,
        title: S[`Title${n}`],
        sub: S[`Sub${n}`],
        desc: S[`Desc${n}`],
      };
    });
  }, [L, language]);
  const maxSteps = slides.length;

  // 自動輪播（開啟時才跑）
  useEffect(() => {
    if (!open) return undefined;
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % maxSteps);
    }, 4000);
    return () => clearInterval(timer);
  }, [open, maxSteps]);

  const title = mode === "signup" ? L.TitleSignup : L.TitleLogin;
  const confirmLabel = mode === "signup" ? L.ConfirmSignup : L.ConfirmLogin;

  const handleClose = () => {
    if (dontShowAgain && dismissKey) localStorage.setItem(dismissKey, "1");
    onClose && onClose();
  };

  const current = slides[activeStep] || {};

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center", ...TYPO.headingM, color: COLORS.black, pb: 0.5 }}>
          {title}
        </DialogTitle>

        <DialogContent>
          <MDBox textAlign="center" px={{ xs: 0.5, sm: 2 }}>
            <MDBox
              component="img"
              src={current.img}
              alt={current.title}
              onClick={() => setZoomImg(current.img)}
              sx={{
                width: "100%",
                maxHeight: 360,
                objectFit: "contain",
                cursor: "pointer",
                mb: 2.5,
              }}
            />
            <MDTypography sx={{ ...TYPO.displayM, color: COLORS.main, mb: 1 }}>
              {current.title}
            </MDTypography>
            <MDTypography sx={{ ...TYPO.title, color: COLORS.black, mb: 1 }}>
              {current.sub}
            </MDTypography>
            <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.grayDark, maxWidth: 440, mx: "auto" }}>
              {current.desc}
            </MDTypography>
          </MDBox>

          <MobileStepper
            variant="dots"
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            sx={{
              mt: 3,
              background: "transparent",
              "& .MuiMobileStepper-dot": {
                backgroundColor: COLORS.grayLighter,
                width: 8,
                height: 8,
                margin: "0 4px",
              },
              "& .MuiMobileStepper-dotActive": { backgroundColor: COLORS.main },
            }}
            nextButton={
              <Button
                size="small"
                onClick={() => setActiveStep((prev) => (prev + 1) % maxSteps)}
                sx={{ ...TYPO.bodyM, color: COLORS.main, textTransform: "none", minWidth: 64 }}
              >
                {L.Next}
              </Button>
            }
            backButton={
              <Button
                size="small"
                onClick={() => setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps)}
                sx={{ ...TYPO.bodyM, color: COLORS.grayDark, textTransform: "none", minWidth: 64 }}
              >
                {L.Back}
              </Button>
            }
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 0, justifyContent: "space-between" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                sx={{ color: COLORS.grayMedian, "&.Mui-checked": { color: COLORS.main } }}
              />
            }
            label={L.DontShow}
            sx={{ "& .MuiFormControlLabel-label": { ...TYPO.bodyS, color: COLORS.grayDark } }}
          />
          <Button
            onClick={handleClose}
            disableRipple
            disableElevation
            sx={{
              ...TYPO.bodyM,
              color: `${COLORS.white} !important`,
              backgroundColor: "transparent !important",
              backgroundImage: `${COLORS.gradient} !important`,
              borderRadius: `${RADIUS.xl}px`,
              px: 3,
              py: 1,
              textTransform: "none",
              boxShadow: "none !important",
              opacity: "1 !important",
              filter: "none !important",
              transition: "none !important",
              // 完全不要 hover 效果：所有狀態都鎖回原樣（!important 壓制 MUI 預設）
              "&:hover, &:focus, &:active, &.Mui-focusVisible": {
                color: `${COLORS.white} !important`,
                backgroundColor: "transparent !important",
                backgroundImage: `${COLORS.gradient} !important`,
                opacity: "1 !important",
                boxShadow: "none !important",
                filter: "none !important",
              },
            }}
          >
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 放大圖片 Dialog */}
      <Dialog open={Boolean(zoomImg)} onClose={() => setZoomImg(null)} maxWidth="md">
        <DialogContent sx={{ textAlign: "center" }}>
          <img
            src={zoomImg}
            alt="Zoomed"
            style={{ width: "100%", height: "auto", maxHeight: "80vh", objectFit: "contain" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

IntroCarouselModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  mode: PropTypes.oneOf(["login", "signup"]),
  dismissKey: PropTypes.string,
};
